import React, { FC, useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { FacebookCircleIcon } from '@/atoms/Icons/FacebookCircleIcon'
import { LinkedInLettersIcon } from '@/atoms/Icons/LinkedInLettersIcon'
import { TwitterXIcon } from '@/atoms/Icons/TwitterXIcon'
import { Image } from '@/atoms/Image'
import { IconColor } from '@/atoms/utils'
import { paths } from '@/constants'
import { Modal } from '@/molecules/Modal'
import { useToast } from '@/molecules/Toast'
import { useUser } from '@/services/UserService'
import { SweepstakeObject } from '@/types/codegen-federation'
import { useLoginUrl, useSignupUrl } from '@/utils/auth-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { generateShareUrl } from '../../services/ShareUrlService'

interface ShareProps {
  isOpen: boolean
  onClose: () => void
  theatricalSlug: string
  imageUrl: string
  shareText: string
  rulesLink: SweepstakeObject['rulesUrl']
}
interface SocialShare {
  name: string
  icon: JSX.Element
  href?: string
  onClick?: () => void
}

export const ShareGiveawayModal: FC<ShareProps> = ({
  isOpen,
  onClose,
  theatricalSlug,
  imageUrl,
  shareText,
  rulesLink,
}) => {
  const router = useSafeRouter()
  const { signupUrl, trackSignupStarted } = useSignupUrl(router.asPath)
  const { loginUrl, trackLoginStarted } = useLoginUrl(router.asPath)
  const { t } = useTranslate('tickets')
  const { user } = useUser()
  const [shareUrl, setShareUrl] = useState('')

  useEffect(() => {
    const defaultUrl = `${paths.getBaseURL()}${paths.preSales.giveaway(
      theatricalSlug,
    )}?project=${theatricalSlug}&product=presales`
    setShareUrl(defaultUrl)

    if (user?.id) {
      generateShareUrl({
        baseUrl: `https://www.angel.com${paths.preSales.giveaway(theatricalSlug)}`,
        userId: user.id,
        slug: theatricalSlug,
        aliasPrefix: 'hm-',
        utmParams: {
          source: 'user_referral',
          medium: 'giveaway_funnel_web',
          campaign: `${theatricalSlug}_giveaway`,
        },
      }).then((generatedUrl) => {
        if (generatedUrl) setShareUrl(generatedUrl)
      })
    }
  }, [theatricalSlug, user?.id])

  const encodedUrl = encodeURIComponent(shareUrl)
  const text = `${shareText} #AngelStudios\n\n ${shareUrl}`
  const encodedShareText = encodeURIComponent(shareText)

  const shareIcons: SocialShare[] = [
    {
      name: 'Facebook',
      icon: <FacebookCircleIcon size={24} color1={'#fff' as IconColor} color2={'#141414' as IconColor} />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInLettersIcon size={24} color={'#fff' as IconColor} />,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      name: 'Twitter',
      icon: <TwitterXIcon size={24} color={'#fff' as IconColor} />,
      href: `https://twitter.com/intent/tweet?text=${encodedShareText}`,
    },
  ]

  const linkCopied = t('linkCopied', 'Link copied.')
  const toast = useToast()

  const handleCopyClick = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      toast.showToast(linkCopied, { icon: <CopyIcon size={24} color="gray-200" /> })
    })
  }, [toast, text, linkCopied])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="mx-auto"
      childWrapperClassName="!m-0 !p-0"
      panelClasses="!p-0 !w-full rounded-3xl overflow-hidden !max-w-[361px] md:!max-w-[782px]"
    >
      <div className="relative flex flex-col md:flex-row">
        <div
          className="absolute right-4 top-4 z-10 cursor-pointer rounded-full bg-black/20 p-2 md:bg-[#D3D3D6]"
          onClick={onClose}
        >
          <CloseIcon color="black" className="opacity-60" size={18} />
        </div>

        <div className="relative h-[200px] w-full overflow-hidden !bg-[#fff1d5] md:h-[578px] md:max-w-[390px]">
          <Image priority src={imageUrl} alt="" fill className="object-cover" />
        </div>
        {user?.id && (
          <a
            className="homestead-eyebrow-md mx-auto w-fit -translate-y-3 rounded-md bg-homestead-yellow px-2 py-1 uppercase md:hidden"
            href={`/pre-sales/${theatricalSlug}/giveaway/summary`}
            target="_blank"
          />
        )}
        <div className="relative flex w-full flex-col items-center justify-center p-6 pt-8 md:p-8 md:pt-12">
          <FlexColumn className="gap-3 text-center md:gap-4">
            <FlexColumn className="gap-1">
              <h2 className="homestead-title-lg md:homestead-title-xl block font-black text-homestead-primary-blue">
                {t('shareEarn', 'SHARE AND EARN')}
              </h2>
              <p className="!font-[bio-sans] text-lg font-semibold leading-[23.4px] text-giveaway-subtle md:!text-[1.313rem] md:leading-[27.3px]">
                {t('earnEntries', 'Earn 100 Extra Entries')}
              </p>
            </FlexColumn>

            <p className="homestead-paragraph-sm md:homestead-paragraph-md text-giveaway-subtle">
              {t(
                'inviteToWin',
                "Invite your friends and family to check out this offer. If they pre-order tickets, you'll get 100 extra entries to win.",
              )}
            </p>
            <p className="!font-[bio-sans] text-[0.625rem] leading-[15px] text-giveaway-subtle">
              <Translate t={t} i18nKey="shareGiveawayTerms">
                By sharing this promotion, you agree to be bound by the terms of the{' '}
                <Link href={rulesLink ?? ''} target="_blank" className="underline">
                  Official Rules
                </Link>{' '}
                and{' '}
                <Link href={paths.legal.privacyPolicy} target="_blank" className="underline">
                  Privacy Policy
                </Link>
                .
              </Translate>
            </p>

            {!user && (
              <p className="homestead-paragraph-sm md:homestead-paragraph-md mx-2 rounded-lg border-[1.5px] border-[#90E5F6] bg-[#F0FFFE] px-2 py-1 text-giveaway-subtle">
                <Translate t={t} i18nKey="giveawayLoginShare">
                  <a href={loginUrl} onClick={() => trackLoginStarted('PreSalesGiveaway')} className="underline">
                    Login
                  </a>{' '}
                  or{' '}
                  <a href={signupUrl} onClick={() => trackSignupStarted('PreSalesGiveaway')} className="underline">
                    create an account
                  </a>{' '}
                  to participate in this promotion.
                </Translate>
              </p>
            )}
          </FlexColumn>

          {!!user && (
            <FlexColumn className="mt-6 w-full gap-6 md:mt-8 md:gap-4">
              <div className="flex justify-center space-x-4">
                {shareIcons.map(({ name, icon, href }) => (
                  <a
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-[10px] bg-giveaway-primary px-3 py-3.5 transition-colors duration-200 hover:opacity-80"
                  >
                    {icon}
                  </a>
                ))}
              </div>
              <div className="flex w-full items-center rounded-2xl bg-[#E7ECF0] p-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="grow truncate bg-transparent pl-1 text-sm text-giveaway-subtle outline-none"
                />
                <button
                  onClick={handleCopyClick}
                  className="whitespace-nowrap rounded-[10px] bg-giveaway-primary px-[20px] py-[11px] text-sm text-white"
                  aria-label="Copy link"
                >
                  {t('copyShareLinkUrl', 'COPY URL')}
                </button>
              </div>
            </FlexColumn>
          )}
        </div>
      </div>
    </Modal>
  )
}
